import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import HeaderBar from './HeaderBar';
import SideNav from './SideNav';
import DetailsSidebar from './DetailsSidebar';
import RightPanel from './RightPanel';
import MobileClientBanner from './MobileClientBanner';
import SelectClientPage from '../pages/SelectClientPage';
import useAppShellData from '../hooks/useAppShellData';
import { useAdditionalInterests } from '../features/dashboard/hooks/useAdditionalInterests';
import { ViewModeProvider, useViewMode } from '../hooks/useViewMode';
import { useSelectedClient } from '../hooks/useSelectedClient';
import '../styles/layout.css';
import '../styles/mobile.css';

const FULL_WIDTH_ROUTES = ['/reports', '/certificateholders', '/email-templates','/signature'];

function AppLayoutInner() {
  const [navCollapsed, setNavCollapsed] = React.useState<boolean>(true);
  const shell = useAppShellData();
  const { viewMode } = useViewMode();
  const { client: selectedClient, insuredId, hasSelectedClient, setClient } = useSelectedClient();
  const location = useLocation();
  const navigate = useNavigate();

  const handleClientSelected = React.useCallback((client: Parameters<typeof setClient>[0]) => {
    setClient(client);
    navigate('/agentdashboard', { replace: true });
  }, [setClient, navigate]);

  const isFullWidth = FULL_WIDTH_ROUTES.some(r => location.pathname.startsWith(r));
  const shouldShowRightPanel = !isFullWidth && viewMode === 'agent' && hasSelectedClient;

  const [aiFirst, setAiFirst] = React.useState(0);
  const AI_ROWS = 5;

  React.useEffect(() => { setAiFirst(0); }, [insuredId]);

  const { additionalInterests, rawAdditionalInterests, isLoading: isAdditionalInterestsLoading, refetch: refetchAdditionalInterests, totalCount: aiTotalCount } = useAdditionalInterests(shouldShowRightPanel, insuredId, aiFirst, AI_ROWS);

  if (!hasSelectedClient) {
    return <SelectClientPage />;
  }

  const bodyClass = [
    'app-body',
    navCollapsed ? 'app-body--navCollapsed' : '',
    isFullWidth ? 'app-body--fullWidth' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="app-shell">
      <HeaderBar
        onToggleNav={() => setNavCollapsed((v) => !v)}
        onClientSelected={handleClientSelected}
        selectedClientName={selectedClient?.name}
      />
      <div className={bodyClass}>
        {!navCollapsed && (
          <div
            className="app-sidenav-backdrop"
            onClick={() => setNavCollapsed(true)}
          />
        )}
        <SideNav 
          collapsed={navCollapsed} 
          onNavLinkClick={() => setNavCollapsed(true)}
          onExpandRequest={() => setNavCollapsed(false)}
        />
        {!isFullWidth && hasSelectedClient && (
          <DetailsSidebar
            client={selectedClient!}
            agency={shell.agency}
            agent={shell.agent}
            certificateSummary={shell.certificateSummary}
          />
        )}
        {!isFullWidth && hasSelectedClient && <MobileClientBanner client={selectedClient!} />}
        <main className="app-main" role="main">
          <Outlet />
        </main>
        {shouldShowRightPanel && (
          <RightPanel
            linkedPolicies={shell.linkedPolicies}
            additionalInterests={additionalInterests}
            rawAdditionalInterests={rawAdditionalInterests}
            insuredId={insuredId}
            isAdditionalInterestsLoading={isAdditionalInterestsLoading}
            onRefetchAdditionalInterests={refetchAdditionalInterests}
            insuredDatabaseId={selectedClient?.id}
            insuredEmail={selectedClient?.email}
            insuredFirstName={selectedClient?.primaryContactName}
            insuredLastName={(selectedClient as any)?.lastName}
            insuredCommercialName={selectedClient?.name}
            aiFirst={aiFirst}
            aiRows={AI_ROWS}
            aiTotalCount={aiTotalCount}
            onAiPageChange={setAiFirst}
          />
        )}
      </div>
    </div>
  );
}

export default function AppLayout() {
  return (
    <ViewModeProvider>
      <AppLayoutInner />
    </ViewModeProvider>
  );
}
